import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AiFillCaretDown, AiFillCaretUp, AiFillFilter } from 'react-icons/ai';
import Pagination from './Pagination';
import { cx, useAnchorPosition, useDismiss } from './hooks';

const getValue = (record, dataIndex) => {
  if (dataIndex === undefined || dataIndex === null) return undefined;
  const path = Array.isArray(dataIndex) ? dataIndex : [dataIndex];
  return path.reduce((obj, key) => (obj == null ? undefined : obj[key]), record);
};

const columnId = (column, index) =>
  column.key ?? (Array.isArray(column.dataIndex) ? column.dataIndex.join('.') : column.dataIndex) ?? index;

/** Header filter button with a checkbox list (column.filters + column.onFilter). */
const FilterMenu = ({ filters, selected, onApply }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(selected);
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const position = useAnchorPosition(anchorRef, open, { placement: 'bottom', align: 'end', offset: 4 });

  useDismiss([anchorRef, panelRef], open, () => setOpen(false));

  const toggle = value =>
    setDraft(d => (d.includes(value) ? d.filter(v => v !== value) : [...d, value]));

  return (
    <span className="th-filter" ref={anchorRef}>
      <button
        type="button"
        className={cx(selected.length > 0 && 'is-on')}
        onClick={() => {
          setDraft(selected);
          setOpen(o => !o);
        }}
        aria-label="Filter"
        aria-expanded={open}
      >
        <AiFillFilter />
      </button>
      {open &&
        position &&
        createPortal(
          <div ref={panelRef} className="th-filter-panel" style={position}>
            <div className="th-filter-options">
              {filters.map(f => (
                <label key={String(f.value)} className="checkbox">
                  <input type="checkbox" checked={draft.includes(f.value)} onChange={() => toggle(f.value)} />
                  {f.text}
                </label>
              ))}
            </div>
            <div className="th-filter-actions">
              <button
                type="button"
                className="btn btn-link btn-sm"
                disabled={!draft.length}
                onClick={() => setDraft([])}
              >
                Reset
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => {
                  onApply(draft);
                  setOpen(false);
                }}
              >
                OK
              </button>
            </div>
          </div>,
          document.body
        )}
    </span>
  );
};

/**
 * Data table with client-side sorting, filtering and pagination.
 *
 * columns: [{ title, dataIndex, key, render(value, record, index), sorter(a, b),
 *             defaultSortOrder: 'ascend'|'descend', filters: [{ text, value }],
 *             onFilter(value, record), width, align, ellipsis, fixed: 'left'|'right', className }]
 * pagination: false | { pageSize, current, onChange, total, showSizeChanger, showQuickJumper, showTotal, … }
 *   When pagination.total is larger than dataSource, rows are treated as already paged by the server.
 * scroll: { x } sets the table's minimum width; the wrapper scrolls horizontally.
 */
const Table = ({
  columns = [],
  dataSource = [],
  rowKey = 'key',
  loading = false,
  pagination,
  scroll,
  size,
  className,
  emptyText = 'No data',
  rowClassName,
  onRow,
}) => {
  const rows = Array.isArray(dataSource) ? dataSource : [];

  const initialSort = useMemo(() => {
    const index = columns.findIndex(c => c.defaultSortOrder);
    return index === -1 ? null : { id: columnId(columns[index], index), order: columns[index].defaultSortOrder };
    // Only the first render decides the default sort.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [sort, setSort] = useState(initialSort);
  const [activeFilters, setActiveFilters] = useState({});

  const pageConfig = pagination === false ? null : { pageSize: 10, ...(pagination || {}) };
  const [innerPage, setInnerPage] = useState(pageConfig?.defaultCurrent ?? 1);
  const [innerSize, setInnerSize] = useState(pageConfig?.pageSize ?? 10);

  const processed = useMemo(() => {
    let list = rows;

    columns.forEach((column, index) => {
      const selected = activeFilters[columnId(column, index)];
      if (selected?.length && column.onFilter) {
        list = list.filter(record => selected.some(value => column.onFilter(value, record)));
      }
    });

    if (sort) {
      const index = columns.findIndex((c, i) => columnId(c, i) === sort.id);
      const sorter = columns[index]?.sorter;
      if (typeof sorter === 'function') {
        list = [...list].sort((a, b) => (sort.order === 'descend' ? sorter(b, a) : sorter(a, b)));
      }
    }

    return list;
  }, [rows, columns, activeFilters, sort]);

  const pageSize = innerSize;
  // Rows are already paged by the server only when it reports more rows than we were given.
  const serverPaged = pageConfig?.total !== undefined && pageConfig.total > rows.length;
  const total = serverPaged ? pageConfig.total : processed.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(pageConfig?.current ?? innerPage, totalPages);

  // Go back to page 1 when filtering or sorting changes the row set.
  useEffect(() => {
    if (pageConfig?.current === undefined) setInnerPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters, sort]);

  const visibleRows =
    pageConfig && !serverPaged
      ? processed.slice((currentPage - 1) * pageSize, currentPage * pageSize)
      : processed;

  const keyFor = (record, index) =>
    typeof rowKey === 'function' ? rowKey(record) : record?.[rowKey] ?? index;

  const cycleSort = id => {
    setSort(prev => {
      if (!prev || prev.id !== id) return { id, order: 'ascend' };
      if (prev.order === 'ascend') return { id, order: 'descend' };
      return null;
    });
  };

  const cellClass = column =>
    cx(
      column.align === 'center' && 'is-center',
      column.align === 'right' && 'is-right',
      column.fixed === 'left' && 'is-sticky-left',
      (column.fixed === 'right' || column.fixed === true) && 'is-sticky-right',
      column.className
    );

  return (
    <div className={cx('table-wrap', loading && 'is-loading', className)}>
      <div className="spin-wrap">
        {loading && (
          <div className="spin-mask">
            <span className="spinner" />
          </div>
        )}
        <div className="table-scroll">
          <table className={cx('table', size === 'small' && 'table-sm')} style={scroll?.x ? { minWidth: scroll.x } : undefined}>
            <colgroup>
              {columns.map((column, index) => (
                <col key={columnId(column, index)} style={column.width ? { width: column.width } : undefined} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {columns.map((column, index) => {
                  const id = columnId(column, index);
                  const sortable = typeof column.sorter === 'function';
                  const order = sort?.id === id ? sort.order : null;
                  return (
                    <th
                      key={id}
                      className={cellClass(column)}
                      aria-sort={order ? (order === 'ascend' ? 'ascending' : 'descending') : undefined}
                    >
                      <div className="th-inner">
                        {sortable ? (
                          <button type="button" className="th-sort" onClick={() => cycleSort(id)}>
                            {column.title}
                            <span className="th-sort-arrows" aria-hidden="true">
                              <AiFillCaretUp className={cx(order === 'ascend' && 'is-on')} />
                              <AiFillCaretDown className={cx(order === 'descend' && 'is-on')} />
                            </span>
                          </button>
                        ) : (
                          <span>{column.title}</span>
                        )}
                        {column.filters?.length > 0 && (
                          <FilterMenu
                            filters={column.filters}
                            selected={activeFilters[id] || []}
                            onApply={values => setActiveFilters(prev => ({ ...prev, [id]: values }))}
                          />
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 ? (
                <tr className="table-empty">
                  <td colSpan={columns.length}>{loading ? ' ' : emptyText}</td>
                </tr>
              ) : (
                visibleRows.map((record, rowIndex) => {
                  const extraRowProps = onRow ? onRow(record, rowIndex) : {};
                  return (
                    <tr
                      key={keyFor(record, rowIndex)}
                      {...extraRowProps}
                      className={cx(
                        typeof rowClassName === 'function' ? rowClassName(record, rowIndex) : rowClassName,
                        extraRowProps?.className
                      )}
                    >
                      {columns.map((column, index) => {
                        const value = getValue(record, column.dataIndex);
                        const content = column.render ? column.render(value, record, rowIndex) : value;
                        return (
                          <td
                            key={columnId(column, index)}
                            className={cx(cellClass(column), column.ellipsis && 'is-ellipsis')}
                            title={column.ellipsis && typeof content === 'string' ? content : undefined}
                          >
                            {content ?? ''}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pageConfig && total > 0 && (
        <Pagination
          current={currentPage}
          total={total}
          pageSize={pageSize}
          showSizeChanger={pageConfig.showSizeChanger}
          pageSizeOptions={pageConfig.pageSizeOptions}
          showQuickJumper={pageConfig.showQuickJumper}
          showTotal={pageConfig.showTotal}
          hideOnSinglePage={pageConfig.hideOnSinglePage}
          onChange={(page, newSize) => {
            if (pageConfig.current === undefined) setInnerPage(page);
            if (newSize !== pageSize) setInnerSize(newSize);
            pageConfig.onChange?.(page, newSize);
          }}
        />
      )}
    </div>
  );
};

export default Table;
